import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { fmtSoldi, fmtQta, ND } from '../../lib/acquisti';

const Kpi = ({ label, valore, nota, testid }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="font-bold tabular-nums" data-testid={testid}>{valore}</p>
    {nota && <p className="text-[11px] text-muted-foreground">{nota}</p>}
  </div>
);

const fmtData = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '');
const fmtPct = (v) => (v === null || v === undefined ? ND : `${String(v).replace('.', ',')}%`);

/** Quanto esce davvero per il personale in un mese: totale, indici e composizione. */
export const CostoPersonaleCard = ({ mese }) => {
  const k = mese.kpi || {};
  const totale = mese.costo_totale ?? mese.totale ?? 0;
  const prec = mese.confronto?.precedente;
  const voci = [
    { label: 'Buste paga', valore: mese.buste_paga, nota: 'retribuzioni', testid: 'personale-buste-paga' },
    { label: 'Contributi', valore: mese.contributi, nota: 'INPS, INAIL e TFR', testid: 'personale-contributi' },
    {
      label: 'Personale Extra',
      valore: mese.personale_extra || 0,
      nota: `${mese.n_personale_extra || 0} uscite di cassa`,
      testid: 'personale-extra',
      accento: true,
    },
  ];

  return (
    <div className="space-y-3" data-testid={`personale-card-${mese.anno}-${mese.mese}`}>
      <div>
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Soldi usciti per il personale
        </p>
        <p className="text-3xl font-bold tabular-nums" data-testid="personale-costo">
          {fmtSoldi(totale)}
        </p>
        <p className="text-[11px] text-muted-foreground">
          buste paga + contributi + personale extra
        </p>
        {prec?.disponibile ? (
          <p className={`text-xs font-semibold flex items-center gap-1 ${prec.delta > 0 ? 'text-red-700' : 'text-emerald-600'}`}>
            {prec.delta > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {prec.delta > 0 ? '+' : ''}{fmtSoldi(prec.delta)}
            {prec.delta_pct !== null && ` (${prec.delta_pct > 0 ? '+' : ''}${prec.delta_pct}%)`}
            <span className="text-muted-foreground font-normal">vs {prec.label}</span>
          </p>
        ) : (
          <p className="text-[11px] text-muted-foreground">Nessun mese precedente per il confronto</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 pt-2 border-t">
        <Kpi
          label="Incidenza sul venduto"
          valore={fmtPct(k.incidenza_totale_pct)}
          nota={k.venduto ? `venduto ${fmtSoldi(k.venduto)}` : 'venduto non disponibile'}
          testid="personale-incidenza"
        />
        <Kpi
          label="Costo per ora"
          valore={k.costo_ora_totale ? fmtSoldi(k.costo_ora_totale) : ND}
          nota={`${fmtQta(k.ore)} ore lavorate`}
          testid="personale-costo-ora"
        />
        <Kpi
          label="Costo per coperto"
          valore={k.costo_per_coperto_totale ? fmtSoldi(k.costo_per_coperto_totale) : ND}
          nota={`${fmtQta(k.coperti)} coperti`}
          testid="personale-costo-coperto"
        />
        <Kpi
          label="Ricavo per ora"
          valore={k.ricavo_per_ora ? fmtSoldi(k.ricavo_per_ora) : ND}
          nota="produttività"
          testid="personale-ricavo-ora"
        />
        <Kpi label="Ore per coperto" valore={fmtQta(k.ore_per_coperto)} nota={`${k.giornate} giornate aperte`} />
        <Kpi label="Dipendenti" valore={mese.n_dipendenti ?? ND} nota={mese.azienda?.nome || ''} />
      </div>

      <div className="space-y-2 pt-2 border-t" data-testid="personale-composizione">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Com’è composta l’uscita del mese
        </p>
        {voci.map((v) => {
          const quota = totale ? Math.round(((v.valore || 0) / totale) * 1000) / 10 : 0;
          return (
            <div key={v.label} className="space-y-1">
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span className={v.accento && v.valore > 0 ? 'text-primary font-medium' : ''}>
                  {v.label}
                  <span className="text-muted-foreground text-[11px]"> · {v.nota}</span>
                </span>
                <span className="font-semibold tabular-nums shrink-0" data-testid={v.testid}>
                  {fmtSoldi(v.valore)}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                <div
                  className={`h-full rounded-full ${v.accento ? 'bg-amber-500' : 'bg-primary'}`}
                  style={{ width: `${quota}%` }}
                />
              </div>
              <p className="text-[10px] text-muted-foreground">
                {quota.toFixed(1).replace('.', ',')}% del totale
              </p>
            </div>
          );
        })}
      </div>

      {mese.personale_extra_movimenti?.length > 0 && (
        <div className="space-y-1 pt-2 border-t" data-testid="personale-extra-movimenti">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Dettaglio Personale Extra
          </p>
          {mese.personale_extra_movimenti.map((u, i) => (
            <div key={i} className="flex items-baseline justify-between gap-2 text-xs">
              <span className="min-w-0 truncate">
                <span className="text-muted-foreground">{fmtData(u.data)}</span> {u.descrizione}
                {u.metodo_pagamento ? <span className="text-muted-foreground"> · {u.metodo_pagamento}</span> : null}
              </span>
              <span className="font-semibold tabular-nums shrink-0">{fmtSoldi(u.importo)}</span>
            </div>
          ))}
        </div>
      )}

      {mese.valori_corretti_a_mano?.length > 0 && (
        <p className="text-[11px] text-amber-700">
          Valori corretti a mano rispetto al PDF: {mese.valori_corretti_a_mano.join(', ')}
        </p>
      )}
    </div>
  );
};

export default CostoPersonaleCard;
